/**
 * First-party analytics sender. Events go only to SIIS's own database through the
 * validating `log_analytics_event` function. No third-party vendor, no cookies, no IP,
 * no personal data. Gated by featureFlags (ANALYTICS_TRACKING_ENABLED + pilot allowlist),
 * visitor opt-out, Global Privacy Control, and Admin Preview suppression.
 */
import { supabase } from "@/integrations/supabase/client";
import { ANALYTICS_PILOT_ATHLETE_IDS, ANALYTICS_TRACKING_ENABLED } from "@/lib/featureFlags";
import { isAnalyticsSuppressed } from "@/lib/analytics";
import { isDemoRoute } from "@/lib/demoSafety";
import {
  type Attribution, OPT_OUT_KEY, captureAttribution, deviceCategory, isOptedOut, isSessionExpired,
  isVisitorExpired, mayTrack, mergeSessionAttribution, pathOnly,
} from "@/lib/analyticsTracker";
import { purgeExpiredFollowAttribution, stashFollowAttribution, takeFollowAttribution } from "@/lib/followAttribution";

export type FirstPartyEvent =
  | "athlete_profile_view" | "share_click" | "social_link_click" | "highlight_click" | "related_athlete_click"
  | "follow_click" | "follow_complete" | "unfollow" | "back_her_game_open" | "direct_support_click"
  | "collection_view" | "product_view" | "variant_select" | "add_to_bag" | "checkout_start"
  | "work_with_her_open" | "inquiry_start" | "inquiry_submit";

const VISITOR_KEY = "siis_a_visitor"; // {id, created}
const SESSION_KEY = "siis_a_session"; // {id, last}
const ATTR_KEY = "siis_a_attr";

const safe = <T,>(fn: () => T, fallback: T): T => { try { return fn(); } catch { return fallback; } };
const read = <T,>(store: Storage, key: string): T | null => safe(() => JSON.parse(store.getItem(key) ?? "null") as T | null, null);
const write = (store: Storage, key: string, value: unknown) => safe(() => store.setItem(key, JSON.stringify(value)), undefined);

const gpc = () => typeof navigator !== "undefined" && (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;

export const globalPrivacyControlOn = gpc;
export const analyticsOptedOut = (): boolean =>
  typeof window === "undefined" || isOptedOut({ optOutFlag: safe(() => localStorage.getItem(OPT_OUT_KEY), null), globalPrivacyControl: gpc() });

/** Opting out stops analytics only and clears SIIS analytics identifiers from this browser. */
export const setAnalyticsOptOut = (optOut: boolean) => {
  safe(() => {
    if (optOut) {
      localStorage.setItem(OPT_OUT_KEY, "1");
      localStorage.removeItem(VISITOR_KEY);
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(ATTR_KEY);
      localStorage.removeItem("siis_a_follow_attr");
    } else localStorage.removeItem(OPT_OUT_KEY);
  }, undefined);
};

export const getVisitorId = (now = Date.now()): string => {
  const v = read<{ id: string; created: number }>(localStorage, VISITOR_KEY);
  if (v && !isVisitorExpired(v.created, now)) return v.id;
  const id = crypto.randomUUID();
  write(localStorage, VISITOR_KEY, { id, created: now });
  return id;
};

const getSessionId = (now = Date.now()): string => {
  const s = read<{ id: string; last: number }>(sessionStorage, SESSION_KEY);
  const id = s && !isSessionExpired(s.last, now) ? s.id : crypto.randomUUID();
  write(sessionStorage, SESSION_KEY, { id, last: now });
  return id;
};

export const getSessionAttribution = (): Attribution => read<Attribution>(sessionStorage, ATTR_KEY) ?? {};

/** Called once per page load: records where this visit came from (kept in this tab only). */
export const captureArrival = () => {
  if (typeof window === "undefined" || isDemoRoute(window.location.pathname)) return;
  safe(() => purgeExpiredFollowAttribution(localStorage), undefined);
  if (typeof window === "undefined" || analyticsOptedOut()) return;
  const arrival = captureAttribution(window.location.href, document.referrer, window.location.hostname);
  write(sessionStorage, ATTR_KEY, mergeSessionAttribution(read<Attribution>(sessionStorage, ATTR_KEY), arrival));
};

/** QR redirects know their code server-side; record it as this session's source. */
export const setQrAttribution = (qr: { code_id: string; placement: string; campaign: string }) => {
  if (typeof window === "undefined" || analyticsOptedOut()) return;
  write(sessionStorage, ATTR_KEY, {
    utm_source: "qr", utm_medium: qr.placement, utm_campaign: qr.campaign,
    qr_code_id: qr.code_id, campaign: qr.campaign.toLowerCase(),
  } satisfies Attribution);
};

export const shouldSend = (athleteId?: string | null) =>
  typeof window !== "undefined" &&
  mayTrack({
    enabled: ANALYTICS_TRACKING_ENABLED,
    pilotIds: ANALYTICS_PILOT_ATHLETE_IDS,
    athleteId,
    optedOut: analyticsOptedOut(),
    fixturePage: isAnalyticsSuppressed(),
  });

/** Fire-and-forget. One retry with the same event_id; the server de-duplicates. */
export const sendEvent = (
  event: FirstPartyEvent,
  opts: { athleteId: string; metadata?: Record<string, string | number | boolean> },
): void => {
  if (!shouldSend(opts.athleteId)) return;
  const now = Date.now();
  const payload = {
    event_id: crypto.randomUUID(),
    event_name: event,
    visitor_id: getVisitorId(now),
    session_id: getSessionId(now),
    athlete_id: opts.athleteId,
    path: pathOnly(window.location.pathname),
    device_category: deviceCategory(window.innerWidth),
    metadata: opts.metadata ?? {},
    ...getSessionAttribution(),
  };
  const attempt = (n: number) =>
    supabase.rpc("log_analytics_event", { p_event: payload }).then(({ error }) => {
      if (error && n === 0) setTimeout(() => attempt(1), 1500);
    }, () => { if (n === 0) setTimeout(() => attempt(1), 1500); });
  void attempt(0);
};

/** After a follow saves: copy this visit's source onto the fan's own follow-history row. */
export const attachFollowAttribution = (athleteId: string, override?: Attribution | null) => {
  if (!shouldSend(athleteId)) return;
  const attr = override && Object.keys(override).length ? override : getSessionAttribution();
  if (!Object.keys(attr).length) return;
  void supabase.rpc("attach_follow_attribution", { p_athlete_id: athleteId, p_attr: attr });
};

/** Admin-only (server-checked): mark this browser's visitor ID as QA traffic for N hours. */
export const registerQaVisitor = async (hours = 24) =>
  supabase.rpc("register_analytics_qa_visitor", { p_visitor_id: getVisitorId(), p_hours: hours });

/** Signed-out Follow: keep this visit's source for the sign-in tab, bound to this intent. */
export const stashFollowSource = (nonce: string, athleteId: string) =>
  safe(() => stashFollowAttribution(localStorage, nonce, athleteId, getSessionAttribution(), shouldSend(athleteId)), undefined);

/** After the magic-link Follow saves: single-use, same intent/athlete, unexpired only. */
export const takeFollowSource = (nonce: string | undefined, athleteId: string) =>
  safe(() => takeFollowAttribution(localStorage, nonce, athleteId), null);
